import React, {useState, useEffect} from 'react';
import post from '../utils/post.js';
import formatMoney from '../utils/formatMoney.js';
import AppHeader from '../components/AppHeader.js';
import Calendar from '../components/Calendar.js';
import HomeRes from '../components/HomeRes.js';
import ConfirmPopup from '../components/ConfirmPopup.js';
import HomeOccupancyHeatmap from '../components/HomeOccupancyHeatmap.js';
import UserAvatar from '../components/UserAvatar.js';
import IconBolt from '../components/IconBolt.js';
import useCountdownRefresh from '../utils/useCountdownRefresh.js';
import '../styles/home.scss';

const REFRESH_INTERVAL = 14;

function formatShortWeekday(date) {
	const shortWeekday = date.toLocaleDateString('cs-CZ', {weekday: 'short'});
	return shortWeekday.charAt(0).toUpperCase() + shortWeekday.slice(1);
}

function renderLockedText(text, notPayed = false) {
	if (!text) return '';
	const bolt = notPayed ? <IconBolt className="home-slot__lock-bolt" /> : '';
	const match = text.match(/^(.*?)\+(\d+)(.*)$/);
	if (match) {
		const [, before, count, after] = match;
		return (
			<>
				{before}
				<span className="fnt_yellow">+{count}</span>
				{after}
				{bolt}
			</>
		);
	}
	return (
		<>
			{text}
			{bolt}
		</>
	);
}

export default function Home({usrInfo, timerCnt}) {
	const messageDismissDuration = 260;
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [dataMessages, setDataMessages] = useState([]);
	const [dismissingMessageIds, setDismissingMessageIds] = useState([]);
	const [dataHours, setDataHours] = useState([]);
	const [showCal, setShowCal] = useState(false);
	const [today, setToday] = useState(new Date().getDate() + '.' + (new Date().getMonth() + 1) + '.' + new Date().getFullYear());
	const [todayShortName, setTodayShortName] = useState(formatShortWeekday(new Date()));
	const [now, setNow] = useState(new Date().getDate() + '.' + (new Date().getMonth() + 1) + '.' + new Date().getFullYear());
	const [reservation, setReservation] = useState({});
	const [showRes, setShowRes] = useState(false);
	const [usrCnt, setUsrCnt] = useState(0);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const [showOccupancyHeatmap, setShowOccupancyHeatmap] = useState(false);
	const {secondsLeft: counterCnt, restart: restartCountdown} = useCountdownRefresh(REFRESH_INTERVAL, () => getData(today));

	useEffect(() => {
		getData(today);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getData = (DT) => {
		setLoading(true);

		let currentDate = new Date().getDate() + '.' + (new Date().getMonth() + 1) + '.' + new Date().getFullYear();

		if (now !== currentDate) {
			setNow(currentDate);
			setToday(currentDate);
			DT = currentDate;
		}

		let parts = DT.split('.');
		let d = new Date(parts[2], parts[1] - 1, parts[0]);
		setTodayShortName(formatShortWeekday(d));

		post('getListReservation', {DT: DT})
			.then((res) => {
				setData(res.data.data);
				setDataMessages(res.data.data_messages);
				setDataHours(res.data.data_hours.data);
				setUsrCnt(res.data.data_hours.data_cnt);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const handleRefresh = () => {
		getData(today);
		restartCountdown();
	};

	const handleReaded = (ID) => {
		if (dismissingMessageIds.includes(ID)) {
			return;
		}

		setDismissingMessageIds((prevRows) => {
			if (prevRows.includes(ID)) {
				return prevRows;
			}

			return [...prevRows, ID];
		});

		post('saveMessagesUsrReaded', {ID: ID})
			.then((res) => {
				setTimeout(() => {
					setDataMessages((prevRows) => prevRows.filter((item) => item.ID !== ID));
					setDismissingMessageIds((prevRows) => prevRows.filter((itemId) => itemId !== ID));
				}, messageDismissDuration);
			})
			.catch((error) => {
				setDismissingMessageIds((prevRows) => prevRows.filter((itemId) => itemId !== ID));
				console.error(error);
			});
	};

	const showCalendar = () => {
		setShowCal(true);
	};

	const closeCal = () => {
		setShowCal(false);
	};

	const todayChange = (val) => {
		setToday(val);
		getData(val);
		restartCountdown();
	};

	const setDTToNow = () => {
		todayChange(now);
		restartCountdown();
	};

	const addUpdateReservation = (item) => {
		if (parseInt(item.CANADD) === 1) {
			const isAdminUser = parseInt(usrInfo.sysAdmin) === 1 || parseInt(usrInfo.usrId) === 1 || parseInt(usrInfo.usrId) === 4;
			const isLockCreator = (parseInt(usrInfo.usrId) || 0) === (parseInt(item.CRBY) || 0);
			const isInstructorUser = parseInt(usrInfo.instructor) === 1;
			const currentUser = item.USERS?.find((item2) => parseInt(item2.USRID) === parseInt(usrInfo.usrId));
			const currentUserInLockedEvent = currentUser && parseInt(item.LOCKED) === 1 && item.LOCK_SOURCE === 'reservation_lock';
			const canEditLockEvent = parseInt(item.HAS_LOCK_EVENT) === 1 && ((isAdminUser && parseInt(item.LOCKED_TYPEID) === 2) || (parseInt(item.LOCKED_TYPEID) === 1 && isInstructorUser && isLockCreator));
			const canEditOwnLockedReservation = parseInt(item.LOCKED) === 1 && item.LOCK_SOURCE === 'reservation' && currentUser && parseInt(item.LOCKED_ID) === parseInt(currentUser.ID) && parseInt(item.CRBY) === parseInt(usrInfo.usrId);

			if (currentUser && (parseInt(item.LOCKED) === 0 || canEditOwnLockedReservation || currentUserInLockedEvent)) {
				setReservation({id: currentUser.ID, dt: currentUser.RESERVATIONDT, tm: currentUser.RESERVATIONDT_TIMES, lng: currentUser.RESERVATIONLNG, addPerson: currentUser.ADD_PERSON, lock: 0, usr_max: data.USERS_MAX, lessonNote: currentUser.LESSONNOTE || '', slotLockedByEvent: currentUserInLockedEvent ? 1 : 0, instructorClients: data.instructorClients || [], selectedClientIds: currentUser.SELECTED_CLIENT_IDS || []});
				setShowRes(true);
			} else if (canEditLockEvent) {
				setReservation({id: item.LOCKED_ID, dt: item.DT1, tm: item.LOCKED_DT1S, lng: item.LNG, addPerson: 0, lock: item.LOCKED_TYPEID, usr_max: data.USERS_MAX, lessonNote: item.LESSONNOTE, slotLockedByEvent: 0, instructorClients: data.instructorClients || [], selectedClientIds: []});
				setShowRes(true);
			} else if (parseInt(item.LOCKED) === 0) {
				setReservation({id: 0, dt: item.DT1, tm: item.DT1S, lng: data.RESERV_LNG_LAST, addPerson: 0, lock: 0, usr_max: data.USERS_MAX, lessonNote: '', slotLockedByEvent: 0, instructorClients: data.instructorClients || [], selectedClientIds: []});
				setShowRes(true);
			}
		}
	};

	const closeRes = () => {
		setShowRes(false);
	};

	const updateReservations = (ID, DATE, LNG, ADDPERSON, LOCK, LESSONNOTE, SELECTEDCLIENTIDS = []) => {
		setLoading(true);

		return post('saveReservation', {DT: today, ID: ID, DATE: DATE, LNG: LNG, ADDPERSON: ADDPERSON, LOCK: LOCK, LESSONNOTE: LESSONNOTE, SELECTEDCLIENTIDS: SELECTEDCLIENTIDS})
			.then((res) => {
				setData(res.data.data.data);
				setDataMessages(res.data.data.data_messages);
				setDataHours(res.data.data.data_hours.data);
				setUsrCnt(res.data.data.data_hours.data_cnt);
				setLoading(false);
				return res;
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
				throw error;
			});
	};

	const deleteReservations = (ID, LOCK) => {
		setLoading(true);

		post('deleteReservation', {DT: today, ID: ID, LOCK: LOCK})
			.then((res) => {
				setData(res.data.data.data);
				setDataMessages(res.data.data.data_messages);
				setDataHours(res.data.data.data_hours.data);
				setUsrCnt(res.data.data.data_hours.data_cnt);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const prevDay = () => {
		let parts = today.split('.');
		let d = new Date(parts[2], parts[1] - 1, parts[0]);
		d.setDate(d.getDate() - 1);
		let newDate = d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();

		setToday(newDate);
		getData(newDate);
		restartCountdown();
	};

	const nextDay = () => {
		let parts = today.split('.');
		let d = new Date(parts[2], parts[1] - 1, parts[0]);
		d.setDate(d.getDate() + 1);
		let newDate = d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();

		setToday(newDate);
		getData(newDate);
		restartCountdown();
	};

	const openOccupancyHeatmap = () => {
		setShowOccupancyHeatmap(true);
	};

	const closeOccupancyHeatmap = () => {
		setShowOccupancyHeatmap(false);
	};

	const showNotPayedDetailPop = () => {
		setShowConfirmPopup(true);
	};

	const closeConfirmPopup = () => {
		setShowConfirmPopup(false);
	};

	const renderReservationSlot = (item, index) => {
		let listUsers = [];

		item.ADDPERSON_LOCK = data.ADDPERSON_LOCK;

		const isReservationLocked = parseInt(item.LOCKED) === 1 && item.LOCK_SOURCE === 'reservation';

		item.USERS?.forEach((item2, index2) => {
			// Rezervace, ktera slot zamyka, je uz zobrazena v hlavicce zamku - ostatni lidi ve slotu zobrazit.
			if (isReservationLocked && parseInt(item2.ID) === parseInt(item.LOCKED_ID)) {
				return;
			}

			const selectedClientLabels = Array.isArray(item2.SELECTED_CLIENT_LABELS) ? item2.SELECTED_CLIENT_LABELS : [];
			const addPersonCount = parseInt(item2.ADD_PERSON, 10) || 0;
			const companionLabels = selectedClientLabels.slice(0, addPersonCount);

			if (companionLabels.length > 0) {
				while (companionLabels.length < addPersonCount) {
					companionLabels.push('?');
				}
			}

			const companionLabel = companionLabels.length > 0 ? ` (${companionLabels.join(', ')})` : '';
			const isCurrentUser = parseInt(item2.USRID) === parseInt(usrInfo.usrId);

			listUsers.push(
				<div className="home-slot__user" key={index2}>
					<div className={`fl_l pl_2 ${isCurrentUser ? 'fnt_yellow fnt_b' : ''}`}>
						<UserAvatar photoUrl={item2.PROFILE_PHOTO_URL || ''} displayName={item2.USRNAME} initials={item2.USRINITIALS || ''} className="fl_l mr_05 home-slot__avatar" />
						<span className="fl_l mr_05">{item2.USRNAME}</span>
						{parseInt(item2.NOT_PAYED) === 1 ? (
							<span className="fl_l home-slot__bolt">
								<IconBolt />
							</span>
						) : (
							''
						)}
						{item2.ADD_PERSON > 0 ? (
							<span className="fl_l fnt_yellow mr_05">
								+{item2.ADD_PERSON}
								{companionLabel}
							</span>
						) : (
							''
						)}
						{parseInt(item2.ADD_PERSON_PAYED) === 0 ? (
							<span className="fl_l home-slot__bolt">
								<IconBolt />
							</span>
						) : (
							''
						)}
					</div>
					<div className="zero h0" />
				</div>,
			);
		});

		const hasLockedText = item.LOCKED_TXT !== '';
		const hasUsers = listUsers.length > 0;
		const hasContent = hasLockedText || hasUsers;
		const isAvailable = parseInt(item.ENTRY_PERSON_CNT_OK) === 1 && parseInt(item.LOCKED) === 0 && hasContent;
		const slotStateClassName = isAvailable ? 'is-available' : hasContent ? 'is-full' : 'is-empty';

		return (
			<div className="home-slot" key={index}>
				<div className={`reservation home-slot__card ${slotStateClassName} ${parseInt(item.CANADD) === 1 ? 'cr_p' : 'cr_d'}`} onClick={() => addUpdateReservation(item)}>
					<div className={`reservation_top home-slot__head ${data.CURRENT_HOURMIN === item.DT1S ? 'fnt_orange fnt_b' : ''}`}>
						{data.CURRENT_HOURMIN === item.DT1S ? (
							<div className="reservation_top_perc home-slot__progress">
								<div className={`reservation_top_perc_in home-slot__progress-in ${!hasContent ? 'empty' : ''}`} style={{width: data.CURRENT_HOURMIN_PERC + '%'}}></div>
							</div>
						) : (
							''
						)}
						<span className="fnt_b home-slot__time">
							{item.DT1S} - {item.DT2S}
						</span>
						{item.USERS_CNT > 0 ? (
							<span className="fl_r fnt_1 mt_02 home-slot__capacity">
								{item.USERS_CNT} / {data.USERS_MAX}
							</span>
						) : (
							''
						)}
					</div>
					{hasContent ? (
						<>
							<div className="zero h0" />
							<div className={`reservation_in home-slot__body ${isAvailable ? 'green' : 'red'}`}>
								{hasLockedText ? (
									<>
										<div className="zero h05" />
										<div className={`txt_l home-slot__lock ${parseInt(item.CRBY) === parseInt(usrInfo.usrId) ? 'fnt_yellow fnt_b' : ''}`}>
											{(item.LOCK_USRNAME || '') !== '' ? (
												<UserAvatar photoUrl={item.LOCK_PHOTO_URL || ''} displayName={item.LOCK_USRNAME} initials={item.LOCK_INITIALS || ''} className="home-slot__avatar" />
											) : (
												''
											)}
											<span className="home-slot__lock-text">{renderLockedText(item.LOCKED_TXT, parseInt(item.LOCK_NOT_PAYED) === 1)}</span>
										</div>
										<div className="zero h05" />
									</>
								) : (
									''
								)}
								{hasUsers ? (
									<>
										<div className="zero h05" />
										<span className="fl_l home-slot__users">{listUsers}</span>
										<div className="zero h05" />
									</>
								) : (
									''
								)}
							</div>
							<div className="zero h0" />
						</>
					) : (
						''
					)}
				</div>
			</div>
		);
	};

	let listHours = [];

	dataHours?.forEach((item, index) => {
		listHours.push(renderReservationSlot(item, index));
	});

	let parts1 = today.split('.');
	let parts2 = now.split('.');
	let todayD = new Date(parts1[2], parts1[1] - 1, parts1[0]);
	let nowD = new Date(parts2[2], parts2[1] - 1, parts2[0]);

	let listNotPayedAddPerson = [];
	let totalNotPayedAddPerson = 0;
	let totalNotPayerAddPersonPrices = 0;
	const hasNotPayedAddPersonDetail = data.NOT_PAYED_ADD_PERSON_DETAIL !== '';

	data?.not_payed_add_person?.forEach((line2, index2) => {
		const isFuture = parseInt(line2.IS_FUTURE) === 1;
		const isCompanionRow = parseInt(line2.TYPE) === 2;
		listNotPayedAddPerson.push(
			<div className={`home-balance-row${isCompanionRow ? ' is-companion is-lock' : ' is-self'}`} key={`NOT_PAYED_ADD_PERSON${index2}`}>
				<div className="home-balance-row__main txt_l">
					<span className={`home-balance-row__title${isFuture ? ' is-future' : ''}`}>
						{line2.RESERVATIONDTS} - {line2.RESERVATIONLNG_H}h{line2.ADD_PERSON > 0 ? ` + ${line2.ADD_PERSON}x` : ''}
					</span>
					<div className="home-balance-row__amounts">
						<span className="home-balance-row__amount">{parseInt(line2.PAYED) === 0 ? formatMoney(line2.NOT_PAYED_PRICES_USR) : formatMoney(0)}</span>
						<span className="home-balance-row__amount home-balance-row__amount--accent">{line2.ADD_PERSON > 0 ? formatMoney(line2.NOT_PAYED_PRICES) : formatMoney(0)}</span>
					</div>
				</div>
			</div>,
		);

		if (parseInt(line2.ADD_PERSON_PAYED) === 0) {
			totalNotPayedAddPerson += parseInt(line2.ADD_PERSON);
			totalNotPayerAddPersonPrices += parseFloat(line2.NOT_PAYED_PRICES);
		}

		if (parseInt(line2.PAYED) === 0) {
			++totalNotPayedAddPerson;
			totalNotPayerAddPersonPrices += parseFloat(line2.NOT_PAYED_PRICES_USR);
		}
	});

	let listMessages = [];

	dataMessages?.forEach((item, index) => {
		const isUnread = parseInt(item.READED) !== 1;
		const isDismissing = dismissingMessageIds.includes(item.ID);
		const messageHtml = item.NOTE || item.MESSAGE || item.MSG || '';
		const messageText = messageHtml
			.replace(/<br\s*\/?>/gi, ' ')
			.replace(/&nbsp;/gi, ' ')
			.replace(/<[^>]*>/g, ' ')
			.trim();

		if (messageText === '') {
			return;
		}

		listMessages.push(
			<div className={`home-message-card${isDismissing ? ' home-message-card--dismissing' : ''}`} key={`MESSAGE_${item.ID || index}`}>
				<div className="home-message-card__surface">
					<div className="home-message-card__body txt_l">
						<span dangerouslySetInnerHTML={{__html: messageHtml}}></span>
					</div>
					{isUnread && item.ID ? (
						<div className="home-message-card__actions">
							<button
								className="button home-message-card__button"
								disabled={isDismissing}
								onClick={() => {
									handleReaded(item.ID);
								}}
							>
								Rozumím
							</button>
						</div>
					) : (
						''
					)}
				</div>
			</div>,
		);
	});

	const hasStickyContent = data.PAYED_DAY < 400 || (typeof data.MESSAGE !== 'undefined' && data.MESSAGE !== '') || Number(data.TOPAY_TOTAL || 0) !== 0 || listNotPayedAddPerson.length > 0;

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<Calendar show={showCal} close={closeCal} defaultValue={today} returnValue={todayChange} dataEvents={data.data_cal_events} />
				<HomeRes showRes={showRes} closeRes={closeRes} data={reservation} dataHours={dataHours} updateReservations={updateReservations} deleteReservations={deleteReservations} usrInfo={usrInfo} />
				<HomeOccupancyHeatmap show={showOccupancyHeatmap} close={closeOccupancyHeatmap} />
				<ConfirmPopup showConfirmPopup={showConfirmPopup} closeConfirmPopup={closeConfirmPopup} infoHeader="Nezaplacené vstupy" infoMain="" popupClassName="home-glass-popup home-balance-popup" popupContentClassName="home-glass-popup__content home-balance-popup__content" bodyClassName="home-glass-popup__body home-balance-popup__body" bodyInnerClassName="home-glass-popup__body-in home-balance-popup__body-in">
					{listNotPayedAddPerson.length > 0 ? (
						<div className="home-balance-card home-balance-card--popup home-balance-card--danger">
							<div className="home-balance-card__header home-balance-card__header--popup">
								<div className="home-balance-card__title-wrap">
									<div className="home-balance-card__title">Nezaplacené vstupy</div>
									<div className="home-balance-card__caption">Kompaktní přehled rezervací k doplacení</div>
								</div>
								<div className="home-balance-card__columns" aria-hidden="true">
									<span>Já</span>
									<span>Doprovody</span>
								</div>
							</div>
							<div className="home-balance-card__body home-balance-card__body--popup">{listNotPayedAddPerson}</div>
							{hasNotPayedAddPersonDetail ? <div className="home-balance-card__detail txt_l" dangerouslySetInnerHTML={{__html: data.NOT_PAYED_ADD_PERSON_DETAIL}}></div> : ''}
							<div className="home-balance-card__footer home-balance-card__footer--popup">
								<span className="home-balance-card__total-label">Celkem</span>
								<span className="home-balance-card__total-value">
									{formatMoney(totalNotPayerAddPersonPrices)} ({totalNotPayedAddPerson}x)
								</span>
							</div>
						</div>
					) : (
						''
					)}
				</ConfirmPopup>

				<div className="top home-top">
					{todayD.getTime() >= nowD.getTime() ? (
						<div className="icon_refresh_main home-refresh mr_1" onClick={() => handleRefresh()}>
							<div className="icon_refresh_in">{counterCnt}</div>
							<img className="icon_refresh fl_l cr_p mt_01" alt="" />
						</div>
					) : (
						<div className="icon_refresh_main mr_1"></div>
					)}

					<div className="fl_l txt_c mr_1" style={{width: '3.5rem'}} onClick={() => prevDay()}>
						<img className="icon_left cr_p" alt="" />
					</div>

					<div className="fl_l txt_c mr_1" style={{width: '3.5rem'}} onClick={() => nextDay()}>
						<img className="icon_right cr_p" alt="" />
					</div>

					<div className="cr_p ml_1" onClick={() => showCalendar()}>
						<span className="input-text-date-cal-icon3 fl_l mr_05"></span>
						<span className="fl_l mt_03">
							{today} ({todayShortName})
						</span>
					</div>
					{now !== today ? (
						<span className="fl_l mt_02 ml_1 lnk2 cr_p" onClick={() => setDTToNow()}>
							Dnes
						</span>
					) : (
						''
					)}
					<span className="fl_r mt_02 home-top__meta">
						<span className="home-top__count">{usrCnt}</span>
						<img className="icon1 member home-top__member" alt="" />
						<img className="icon1 stat home-top__stat cr_p" alt="" onClick={() => openOccupancyHeatmap()} />
					</span>
				</div>

				<div className={`page home-page ${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`} style={{paddingTop: '0rem'}}>
					{hasStickyContent ? (
						<div className="sticky home-sticky">
							{data.PAYED_DAY < 400 ? (
								<>
									<div className="home-summary-card home-summary-card--warning" key="PERNAMENTMEMBER">
										<div className="home-summary-card__content txt_l">
											{data.PERNAMENTMEMBER === 0 ? (
												<>
													{/*<span className="home-summary-card__eyebrow">Členství</span>*/}
													<span className="home-summary-card__value">Jednorázové vstupy</span>
												</>
											) : (
												<>
													{/*<span className="home-summary-card__eyebrow">Členství</span>*/}
													<div className="home-summary-card__split">
														<span className="home-summary-card__value">Členství do: {data.PAYEDTO_DTS}</span>
														<span className="home-summary-card__meta home-summary-card__meta--strong">
															Zbývá: {data.PAYED_DAYS} {data.PAYED_DAYS_TXT_D}
															{data.PAYED_DAYS_TXT_H}
														</span>
													</div>
												</>
											)}
										</div>
									</div>
								</>
							) : (
								''
							)}

							{typeof data.MESSAGE !== 'undefined' && data.MESSAGE !== '' ? (
								<>
									<div className="list_new gray home-info-card" key="MESSAGES">
										<div className="list_new_head txt_l">
											<span className="" dangerouslySetInnerHTML={{__html: data.MESSAGE}}></span>
										</div>
									</div>
								</>
							) : (
								''
							)}

							{data.TOPAY_TOTAL > 0 ? (
								<>
									<div className="home-summary-card home-summary-card--danger" key="ESHOP">
										<div className="home-summary-card__content home-summary-card__content--eshop txt_l">
											<div className="home-summary-card__group">
												{/*<span className="home-summary-card__eyebrow">E-shop</span>*/}
												<span className="home-summary-card__label">Nezaplacený nákup</span>
											</div>
											<span className="home-summary-card__value">{data.TOPAY_TOTALS}</span>
										</div>
									</div>
								</>
							) : (
								''
							)}

							{data.TOPAY_TOTAL < 0 ? (
								<>
									<div className="home-summary-card home-summary-card--success" key="ESHOP2">
										<div className="home-summary-card__content home-summary-card__content--eshop txt_l">
											<div className="home-summary-card__group">
												{/*<span className="home-summary-card__eyebrow">E-shop</span>*/}
												<span className="home-summary-card__label">Předplaceno</span>
											</div>
											<span className="home-summary-card__value">{data.TOPAY_TOTALS}</span>
										</div>
									</div>
								</>
							) : (
								''
							)}

							{listNotPayedAddPerson.length > 0 ? (
								<>
									<div className="home-balance-card home-balance-card--danger" key="ADDPERS">
										<div className="home-balance-card__header txt_l">
											<div className="home-balance-card__title-wrap">
												<div className="home-balance-card__title">Nezaplacené vstupy</div>
												<div className="home-balance-card__caption">Rezervace a doprovody čekající na doplacení</div>
											</div>
											<div className="home-balance-card__header-actions">
												<button className="button home-balance-card__detail-button mr_1" onClick={() => showNotPayedDetailPop()}>
													Detail
												</button>
												<span className="home-balance-card__total-value">
													{formatMoney(totalNotPayerAddPersonPrices)} ({totalNotPayedAddPerson}x)
												</span>
											</div>
										</div>
									</div>
								</>
							) : (
								''
							)}
						</div>
					) : (
						''
					)}

					<div className="home-messages">{listMessages}</div>
					<div className="zero h05" />

					<div className="home-slot-grid">{listHours}</div>
				</div>
			</div>
		</>
	);
}
